import {createFragmentContainer, graphql} from 'react-relay';
import {cloneDeep} from 'lodash';
import {ESTIMATION_UNIT} from '../../../../constants';
import util from '../../../shared/util/util';
import {StageLike, stageGroupKeyOf} from '../../../shared/util/projectStageUtil';
import {Moment} from 'moment';
import React, {ReactElement, ReactNode} from 'react';
import {ProjectsLoader_viewer$data} from './__generated__/ProjectsLoader_viewer.graphql';

type CalculatedData = {
	forecast: number;
	remaining: number;
	reported: number;
	tasksCount: number;
	doneTasksCount: number;
	projectPersonsCount: number | null;
};

type CalculatedDataMap = Map<string, CalculatedData>;

type GroupedProjectNode = {
	id: string;
	estimationUnit?: string | null;
	minutesPerEstimationPoint?: number | null;
	projectStartYear?: number | null;
	projectStartMonth?: number | null;
	projectStartDay?: number | null;
	projectEndYear?: number | null;
	projectEndMonth?: number | null;
	projectEndDay?: number | null;
	showProject?: boolean;
};

/** A project group edge, which this loader decorates with the totals of its projects. */
type ProjectGroupEdge = {
	isCalculatedDataReady?: boolean;
	node: {
		projects?: {edges?: ReadonlyArray<{node: GroupedProjectNode}> | null} | null;
		isHourEstimated?: boolean | null;
		minutesPerEstimationPoint?: number | null;
		completion?: number;
		forecast?: number;
		reported?: number;
		remaining?: number;
		startDate?: Moment | null;
		endDate?: Moment | null;
		tasksCount?: number;
		doneTasksCount?: number;
		projectPersonsCount?: number | null;
	};
};

type ProjectEdge = {node: {stage?: StageLike; isInProjectGroup?: boolean | null}};

type Props = {
	/** Injected by LoadMore, which clones the loader with the query's data and its own children. */
	viewer?: ProjectsLoader_viewer$data;
	children?: (result: {data: Array<ProjectGroupEdge | ProjectEdge>}) => ReactNode;
	calculatedDataMap?: CalculatedDataMap | null;
	projectFromOutside?: {edges: ReadonlyArray<ProjectEdge>} | null;
	projectGroupsFromOutside?: {edges: ReadonlyArray<ProjectGroupEdge>} | null;
	project_status: string;
};

const setProjectGroupData = (
	viewer: ProjectsLoader_viewer$data | undefined,
	projectGroup: ProjectGroupEdge,
	calculatedDataMap: CalculatedDataMap | null | undefined
): ProjectGroupEdge => {
	const isHourEstimated = projectGroup.node.projects?.edges?.length
		? projectGroup.node.projects.edges[0].node.estimationUnit === ESTIMATION_UNIT.HOURS
		: null;
	let approvedScope = 0,
		remaining = 0,
		reported = 0,
		startDate: Moment | null = null,
		endDate: Moment | null = null,
		tasksCount = 0,
		doneTasksCount = 0,
		projectPersonsCount: number | null = null,
		factor: number | null | undefined = null;
	projectGroup.isCalculatedDataReady = true;
	projectGroup.node.projects?.edges?.forEach(project => {
		project.node.showProject = true;
		if (isHourEstimated === false && factor === null) {
			factor = project.node.minutesPerEstimationPoint;
		}

		const calculatedData = calculatedDataMap ? calculatedDataMap.get(project.node.id) : null;
		if (!calculatedData) {
			projectGroup.isCalculatedDataReady = false;
		}
		/*
		 * ESTIMATION, TIME RELATED FIELDS
		 */
		approvedScope += calculatedData ? calculatedData.forecast : 0;
		remaining += calculatedData ? calculatedData.remaining : 0;
		reported += calculatedData ? calculatedData.reported : 0;
		/*
		 * DATES
		 */
		if (project.node.projectStartYear) {
			const projectStartDate: Moment | null = util.CreateNonUtcMomentDate(
				project.node.projectStartYear,
				project.node.projectStartMonth,
				project.node.projectStartDay
			);
			if (startDate === null || (startDate !== null && startDate.isAfter(projectStartDate))) {
				startDate = projectStartDate;
			}
		}
		if (project.node.projectEndYear) {
			const projectEndDate: Moment | null = util.CreateNonUtcMomentDate(
				project.node.projectEndYear,
				project.node.projectEndMonth,
				project.node.projectEndDay
			);
			if (endDate === null || (endDate !== null && endDate.isBefore(projectEndDate))) {
				endDate = projectEndDate;
			}
		}
		/*
		 * COUNTS
		 */
		tasksCount += calculatedData ? calculatedData.tasksCount : 0;
		doneTasksCount += calculatedData ? calculatedData.doneTasksCount : 0;
		if (projectPersonsCount === null) {
			projectPersonsCount = calculatedData ? calculatedData.projectPersonsCount : 0;
		}
	});
	projectGroup.node.isHourEstimated = isHourEstimated;
	projectGroup.node.minutesPerEstimationPoint = factor;
	projectGroup.node.completion = util.calculateElementProgress(
		approvedScope,
		reported,
		remaining,
		tasksCount,
		doneTasksCount,
		isHourEstimated,
		factor
	);
	projectGroup.node.forecast = approvedScope;
	projectGroup.node.reported = reported;
	projectGroup.node.remaining = remaining;
	projectGroup.node.startDate = startDate;
	projectGroup.node.endDate = endDate;
	projectGroup.node.tasksCount = tasksCount;
	projectGroup.node.doneTasksCount = doneTasksCount;
	projectGroup.node.projectPersonsCount = projectPersonsCount;

	return projectGroup;
};

const ProjectsLoader = ({
	viewer,
	children,
	calculatedDataMap,
	projectFromOutside,
	projectGroupsFromOutside,
	project_status,
}: Props): ReactElement | null => {
	if (!viewer || !children) {
		return null;
	}
	const projectsAndGroups: Array<ProjectGroupEdge | ProjectEdge> = [];
	if (projectGroupsFromOutside) {
		projectGroupsFromOutside.edges.forEach(pGroup => {
			projectsAndGroups.push(setProjectGroupData(viewer, cloneDeep(pGroup), calculatedDataMap));
		});
	}

	// The second clause here is to avoid an error which occurs if you disable the feature flag while the page is active. When the feature flag is cleaned up, it should similarly be removed.
	if (projectFromOutside) {
		projectFromOutside.edges
			.filter(p => stageGroupKeyOf(p.node) === project_status)
			.filter(p => viewer.client || !p.node.isInProjectGroup)
			.forEach(p => projectsAndGroups.push(p));
	}

	// An unkeyed fragment at the top is rendered exactly like the bare children it wraps.
	return <>{children({data: projectsAndGroups})}</>;
};

export {ProjectsLoader};

export const projectsLoaderQuery = graphql`
	query ProjectsLoader_Query {
		viewer {
			actualPersonId
			component(name: "projects_loader")
			...ProjectsLoader_viewer
		}
	}
`;

graphql`
	fragment ProjectsLoader_financialsMultiCurrencies on ProjectType {
		financialNumbers {
			baselineRevenue
			billablePlannedTimeAndExpenses
			actualRevenue
			remainingRevenue
			forecastRevenue
			recognitionOpenRevenue
			recognitionForecastRevenue
			recognitionLockedRevenue
			plannedCost
			actualCost
			forecastCost
			remainingCost
			plannedProfit
			forecastProfit
			invoiced
			paid
		}
		financialNumbersProjectCurrency {
			baselineRevenue
			billablePlannedTimeAndExpenses
			actualRevenue
			remainingRevenue
			forecastRevenue
			recognitionOpenRevenue
			recognitionForecastRevenue
			recognitionLockedRevenue
			plannedCost
			actualCost
			forecastCost
			remainingCost
			plannedProfit
			forecastProfit
			invoiced
			paid
		}
	}
`;

graphql`
	fragment ProjectsLoader_financialsCompanyCurrency on ProjectType {
		financialNumbers {
			baselineRevenue
			billablePlannedTimeAndExpenses
			actualRevenue
			remainingRevenue
			forecastRevenue
			recognitionOpenRevenue
			recognitionForecastRevenue
			recognitionLockedRevenue
			plannedCost
			actualCost
			forecastCost
			remainingCost
			plannedProfit
			forecastProfit
			invoiced
			paid
		}
	}
`;

graphql`
	fragment ProjectsLoader_project on ProjectType {
		id
		name
		budgetType
		stage {
			id
			name
			profile
		}
		description
		statusColor
		companyProjectId
		readOnlyAccess
		budget
		budgetBaseCurrency
		projectColor
		estimationUnit
		minutesPerEstimationPoint
		projectStartDay
		projectStartMonth
		projectStartYear
		projectEndDay
		projectEndMonth
		projectEndYear
		baselineWinChance
		sprintTimeBox
		currentProjectStatus {
			id
			color
			person {
				id
				firstName
				lastName
			}
		}
		client {
			id
			name
			logoId
			logoDefaultId
		}
		rateCard {
			id
			name
			currency
			parentRateCardId
		}
		progress
		manualProgressOnProjectEnabled
		manualProgressOnPhasesEnabled
		manualProgressOnTasksEnabled
		priorityLevel {
			id
			name
			weight
		}
		projectLabels(first: 10000) {
			edges {
				node {
					id
					label {
						id
						name
						color
					}
				}
			}
		}
		projectPersons(first: 1000000, contactsOnly: true) {
			edges {
				node {
					id
					isContactPerson
					person {
						id
						firstName
						lastName
						profilePictureId
						profilePictureDefaultId
						initials
					}
				}
			}
		}
	}
`;

// This fragment container barely does anything, but because of the funky LoadMore approach taken in overview_projects it still needs to be a fragment container.
export default createFragmentContainer(ProjectsLoader, {
	viewer: graphql`
		fragment ProjectsLoader_viewer on Viewer {
			availableFeatureFlags {
				key
			}
			client {
				id
			}
			company {
				xeroEnabled
			}
		}
	`,
});
